<template>
  <div class="rounded-xl border border-base-300 bg-base-100 p-4">
    <div class="flex justify-between items-center">
      <div class="flex flex-col">
        <h2 class="text-lg font-semibold">{{chartTitle}}</h2>
        <p class="text-xs text-light">{{chartDescription}}</p>
      </div>
      <div class="flex gap-2">
        <AButton class="btn-soft btn-accent btn-xs">Current year</AButton>
        <AButton class="btn-soft btn-accent btn-xs">Historical</AButton>
      </div>
    </div>

    <div class="mt-4 flex items-center justify-between text-sm px-10">
      <!-- Primary (sent / received) -->
      <div class="flex items-center gap-2">
        <StopIcon class="w-3.5 h-3.5 text-primary"/>
        <div>
          <p class="text-xs text-base-content/60">{{quotesLabel}}</p>
          <p class="text-lg font-semibold text-base-content">{{sentData.at(-1) ?? 0}}</p>
        </div>
      </div>

      <!-- Middle curve (quoted) — airline only -->
      <div v-if="middleData.length" class="flex items-center gap-2">
        <StopIcon class="w-3.5 h-3.5 text-warning"/>
        <div>
          <p class="text-xs text-base-content/60">{{middleLabel}}</p>
          <p class="text-lg font-semibold text-base-content">{{middleData.at(-1) ?? 0}}</p>
        </div>
      </div>

      <!-- Secondary (accepted) -->
      <div class="flex items-center gap-2">
        <StopIcon class="w-3.5 h-3.5 text-success"/>
        <div>
          <p class="text-xs text-base-content/60">{{acceptedLabel}}</p>
          <p class="text-lg font-semibold text-base-content">{{acceptedData.at(-1) ?? 0}}</p>
        </div>
      </div>

      <!-- Numeric Badge -->
      <NumericBadge v-if="increase.length > 0" :increase="increase"/>
    </div>
    <div class="mt-4">
      <div v-if="hasData" class="relative h-80">
        <canvas ref="canvasEl"></canvas>
      </div>
      <DashboardChartNoData v-else/>
    </div>

  </div>
</template>

<script setup>
import {computed, nextTick, onActivated, onBeforeUnmount, onDeactivated, ref, shallowRef, watch} from 'vue'
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineController,
  LineElement,
  PointElement,
  Title,
  Tooltip
} from 'chart.js'

import AButton from "@/components/atoms/AButton.vue"
import NumericBadge from "@/components/atoms/NumericBadge.vue"
import {StopIcon} from '@heroicons/vue/24/solid'
import {MONTHS} from "@/common/constants.js"
import DashboardChartNoData from "@/components/atoms/DashboardChartNoData.vue";

const props = defineProps({
  chartTitle: String,
  chartDescription: String,
  quotesLabel: String,
  acceptedLabel: String,
  middleLabel: { type: String, default: '' },
  sentData:    { type: Array, default: () => [] },
  middleData:  { type: Array, default: () => [] },
  acceptedData:{ type: Array, default: () => [] },
  increase:    { type: String, default: '' },
})

// Register chart.js components
ChartJS.register(
    LineController,
    LineElement,
    PointElement,
    LinearScale,
    CategoryScale,
    Tooltip,
    Legend,
    Title
)

const canvasEl = ref(null)
const chartRef = shallowRef(null)

const hasData = computed(() =>
  props.sentData.some(v => v !== 0) ||
  props.middleData.some(v => v !== 0) ||
  props.acceptedData.some(v => v !== 0)
)

function destroyChart() {
  if (chartRef.value) {
    chartRef.value.destroy()
    chartRef.value = null
  }
}

// Null-out zeros that have no non-zero neighbour (flat baseline segments).
// Zeros adjacent to a real value are kept so the line connects properly.
const sparseNonZero = arr => arr.map((v, i) => {
  if (v !== 0) return v
  const prev = arr[i - 1] ?? 0
  const next = arr[i + 1] ?? 0
  return (prev > 0 || next > 0) ? 0 : null
})

function renderChart() {
  if (!canvasEl.value || !hasData.value) {
    destroyChart()
    return
  }

  // Always destroy before creating (prevents "Canvas is already in use")
  destroyChart()

  const ctx = canvasEl.value.getContext('2d')

  try {
    chartRef.value = new ChartJS(ctx, {
      type: 'line',
      data: {
        labels: MONTHS,
        datasets: [
          {
            label: props.quotesLabel || 'Sent',
            data: sparseNonZero(props.sentData),
            borderColor: '#3c89f9',
            backgroundColor: '#3b82f619',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: '#3c89f9',
            pointHoverRadius: 5,
            pointStyle: 'circle',
          },
          ...(props.middleData.length ? [{
            label: props.middleLabel || 'Quoted',
            data: sparseNonZero(props.middleData),
            borderColor: '#f97316',
            backgroundColor: '#f9731619',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: '#f97316',
            pointHoverRadius: 5,
            pointStyle: 'circle',
          }] : []),
          {
            label: props.acceptedLabel || 'Accepted',
            data: sparseNonZero(props.acceptedData),
            borderColor: '#3cf2a4',
            backgroundColor: '#10b98119',
            fill: true,
            tension: 0.4,
            pointRadius: 4,
            pointBackgroundColor: '#3cf2a4',
            pointHoverRadius: 5,
            pointStyle: 'circle',
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {display: false},
          tooltip: {mode: 'index', intersect: false},
          title: {display: false},
        },
        animation: {
          duration: 1200,
          easing: 'easeOutQuart',
        },
        interaction: {
          mode: 'nearest',
          axis: 'x',
          intersect: false,
        },
        scales: {
          y: {
            grid: {drawBorder: false, drawTicks: false},
            beginAtZero: true,
            ticks: {
              stepSize: 5,
              display: false,
              drawBorder: false,
            },
          },
          x: {
            grid: {display: false, drawBorder: false},
          },
        },
      },
    })
  } catch (error) {
    console.error('🔥 Error rendering chart:', error)
  }
}

const nextFrame = () => new Promise(resolve => requestAnimationFrame(resolve))

// 🔁 Re-render chart when props become available
watch(
    () => [props.sentData, props.acceptedData],
    async ([sent, accepted]) => {
      if (sent.length && accepted.length) {
        await nextTick()
        await nextFrame()
        renderChart()
      }
    },
    {immediate: true, deep: false}
)

// Clean up on navigation/unmount
onBeforeUnmount(destroyChart)

// If using <KeepAlive>, also handle activation/deactivation
onDeactivated(destroyChart)
onActivated(async () => {
  await nextTick()
  await nextFrame()
  renderChart()
})
</script>